/**
 * Operator (back-office) cross-tenant directory queries — Tenants / Hotels /
 * Calls. NOT tenant-scoped: these hit `/api/v1/operator/*` (trusted bypass
 * context on the backend) and are gated by `admin:cross_tenant_read`. The call
 * directory is diagnostics only — no transcript/guest content.
 */

import { keepPreviousData, type UseQueryResult, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { queryKeys } from '../query-keys';
import { useApiClient } from '../react/ApiClientProvider';
import {
  type HotelFactsResponse,
  hotelFactsResponseSchema,
  type OperatorCall,
  type OperatorHotel,
  type OperatorMember,
  type OperatorTenant,
  type OperatorUser,
  type OperatorUserStatus,
  operatorCallSchema,
  operatorHotelSchema,
  operatorMemberSchema,
  operatorTenantSchema,
  operatorUserSchema,
  type Paginated,
  paginated,
  type TenantPlan,
  type TenantStatus,
} from '../responses';
import { unwrap } from './_helpers';

/**
 * Filters for the cross-tenant tenants directory. Type alias (not interface) so
 * it carries an implicit index signature for scoped query keys.
 */
export type OperatorTenantsFilters = {
  /** Free-text match against name / slug. */
  q?: string;
  status?: TenantStatus;
  plan?: TenantPlan;
  /** Page size, 1–200 (backend default 50). */
  limit?: number;
  /** Row offset (default 0). */
  offset?: number;
};

/** `GET /operator/tenants` — filtered, searched, paginated partner directory. */
export function useOperatorTenants(
  filters?: OperatorTenantsFilters,
): UseQueryResult<Paginated<OperatorTenant>> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.operatorTenants(filters),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await client.GET('/api/v1/operator/tenants', {
        params: {
          query: {
            q: filters?.q,
            status: filters?.status,
            plan: filters?.plan,
            limit: filters?.limit,
            offset: filters?.offset,
          },
        },
        parseAs: 'json',
      });
      return unwrap(result, paginated(operatorTenantSchema));
    },
  });
}

/** Filters for the cross-tenant hotels directory. */
export type OperatorHotelsFilters = {
  /** Free-text match against hotel name / slug. */
  q?: string;
  /** Narrow to a single partner (tenant) UUID. */
  tenantId?: string;
  /**
   * Narrow to a single hotel lifecycle status. The backend `/operator/hotels`
   * endpoint has no status query param, so this is applied client-side over the
   * loaded page (honest: it never claims a server-side filter that does not exist).
   */
  status?: OperatorHotel['status'];
  limit?: number;
  offset?: number;
};

/** `GET /operator/hotels` — cross-tenant hotels, each carrying its partner. */
export function useOperatorHotels(
  filters?: OperatorHotelsFilters,
): UseQueryResult<Paginated<OperatorHotel>> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.operatorHotels(filters),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await client.GET('/api/v1/operator/hotels', {
        params: {
          query: {
            q: filters?.q,
            tenantId: filters?.tenantId,
            limit: filters?.limit,
            offset: filters?.offset,
          },
        },
        parseAs: 'json',
      });
      return unwrap(result, paginated(operatorHotelSchema));
    },
  });
}

/** Filters for the cross-tenant calls directory. */
export type OperatorCallsFilters = {
  /** Free-text match against call id / partner slug / hotel slug. */
  q?: string;
  status?: OperatorCall['status'];
  direction?: 'inbound' | 'outbound';
  language?: string;
  /** Narrow to a single partner (tenant) UUID. */
  tenantId?: string;
  /** Narrow to a single hotel UUID. */
  hotelId?: string;
  /** ISO started-at lower bound (inclusive). */
  from?: string;
  /** ISO started-at upper bound (inclusive). */
  to?: string;
  limit?: number;
  offset?: number;
};

/** `GET /operator/calls` — cross-tenant call diagnostics (no transcript/PII). */
export function useOperatorCalls(
  filters?: OperatorCallsFilters,
): UseQueryResult<Paginated<OperatorCall>> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.operatorCalls(filters),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await client.GET('/api/v1/operator/calls', {
        params: {
          query: {
            q: filters?.q,
            status: filters?.status,
            direction: filters?.direction,
            language: filters?.language,
            tenantId: filters?.tenantId,
            hotelId: filters?.hotelId,
            from: filters?.from,
            to: filters?.to,
            limit: filters?.limit,
            offset: filters?.offset,
          },
        },
        parseAs: 'json',
      });
      return unwrap(result, paginated(operatorCallSchema));
    },
  });
}

/** Filters for the cross-tenant users directory. */
export type OperatorUsersFilters = {
  /** Free-text match against email / full name. */
  q?: string;
  /** REAL user account status. */
  status?: OperatorUserStatus;
  /**
   * `operator` = users resolved to a `hostesa_admin` membership; `hotel` =
   * everyone else (incl. membership-less users).
   */
  type?: 'operator' | 'hotel';
  /** Page size, 1–200 (backend default 50). */
  limit?: number;
  /** Row offset (default 0). */
  offset?: number;
};

/**
 * `GET /operator/users` — filtered, searched, paginated cross-tenant user
 * directory. Emails are REAL in the payload — mask them in the UI. Operators
 * (`hostesa_admin`) are intentionally not partner-scoped (null tenant fields).
 */
export function useOperatorUsers(
  filters?: OperatorUsersFilters,
): UseQueryResult<Paginated<OperatorUser>> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.operatorUsers(filters),
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await client.GET('/api/v1/operator/users', {
        params: {
          query: {
            q: filters?.q,
            status: filters?.status,
            type: filters?.type,
            limit: filters?.limit,
            offset: filters?.offset,
          },
        },
        parseAs: 'json',
      });
      return unwrap(result, paginated(operatorUserSchema));
    },
  });
}

/** `GET /operator/tenants/{id}` — single partner (tenant) detail. */
export function useOperatorTenant(id: string): UseQueryResult<OperatorTenant> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.operatorTenant(id),
    enabled: id.length > 0,
    queryFn: async () => {
      const result = await client.GET('/api/v1/operator/tenants/{id}', {
        params: { path: { id } },
        parseAs: 'json',
      });
      return unwrap(result, operatorTenantSchema);
    },
  });
}

/** `GET /operator/hotels/{id}` — single hotel detail enriched with its partner. */
export function useOperatorHotel(id: string): UseQueryResult<OperatorHotel> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.operatorHotel(id),
    enabled: id.length > 0,
    queryFn: async () => {
      const result = await client.GET('/api/v1/operator/hotels/{id}', {
        params: { path: { id } },
        parseAs: 'json',
      });
      return unwrap(result, operatorHotelSchema);
    },
  });
}

/** `GET /operator/tenants/{tenantId}/members` — all members of a partner tenant. */
export function useOperatorTenantMembers(tenantId: string): UseQueryResult<OperatorMember[]> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.operatorTenantMembers(tenantId),
    enabled: tenantId.length > 0,
    queryFn: async () => {
      const result = await client.GET('/api/v1/operator/tenants/{tenantId}/members', {
        params: { path: { tenantId } },
        parseAs: 'json',
      });
      return unwrap(result, z.array(operatorMemberSchema));
    },
  });
}

/** `GET /operator/hotels/{id}/setup/facts` — a partner hotel's curated facts (operator view). */
export function useOperatorHotelFacts(hotelId: string): UseQueryResult<HotelFactsResponse> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.operatorHotelFacts(hotelId),
    enabled: hotelId.length > 0,
    queryFn: async () => {
      const result = await client.GET('/api/v1/operator/hotels/{id}/setup/facts', {
        params: { path: { id: hotelId } },
        parseAs: 'json',
      });
      return unwrap(result, hotelFactsResponseSchema);
    },
  });
}
